import { supabase } from "./supabase";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// iOS only exposes the Push API to sites installed to the home screen, so an
// iPhone user needs to be told to install before the permission prompt exists.
export const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

export const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

export const needsHomeScreenInstall = () => isIOS() && !isStandalone();

export const permission = () =>
  pushSupported() ? Notification.permission : "unsupported";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export async function registerServiceWorker() {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (err) {
    console.error("service worker registration failed", err);
    return null;
  }
}

/**
 * Ask for permission, subscribe, and store the subscription.
 * Returns { ok, reason } — reason is one of: unsupported, ios-install,
 * denied, error.
 */
export async function enablePush(userId) {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  if (needsHomeScreenInstall()) return { ok: false, reason: "ios-install" };
  if (!VAPID_PUBLIC_KEY) {
    console.error("VITE_VAPID_PUBLIC_KEY is not set");
    return { ok: false, reason: "error" };
  }

  const result = await Notification.requestPermission();
  if (result !== "granted") return { ok: false, reason: "denied" };

  const registration = await registerServiceWorker();
  if (!registration) return { ok: false, reason: "error" };

  try {
    await navigator.serviceWorker.ready;

    // Reuse an existing subscription; re-subscribing invalidates the old one.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const json = subscription.toJSON();

    // Upsert on endpoint: the same device re-enabling should update its row,
    // not accumulate duplicates that all deliver the same notification.
    const { error } = await supabase.from("push_subscriptions").upsert(
      {
        user_id: userId,
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        last_used_at: new Date().toISOString(),
      },
      { onConflict: "endpoint" }
    );

    if (error) {
      console.error("could not save push subscription", error);
      return { ok: false, reason: "error" };
    }

    return { ok: true };
  } catch (err) {
    console.error("push subscribe failed", err);
    return { ok: false, reason: "error" };
  }
}

export async function disablePush() {
  if (!pushSupported()) return;

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await supabase.from("push_subscriptions").delete().eq("endpoint", endpoint);
}

export async function isSubscribed() {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const registration = await navigator.serviceWorker.getRegistration();
  return !!(await registration?.pushManager.getSubscription());
}

/**
 * Ask the Edge Function to notify the other person in a match.
 *
 * Called by the sender's client rather than a database webhook, so no service
 * key has to live in the database. The function verifies the caller's JWT and
 * that they are actually in the match before sending anything.
 *
 * Best-effort: a failure here must never block the message that was sent.
 */
export async function notifyMatch(type, matchId) {
  try {
    await supabase.functions.invoke("notify", { body: { type, matchId } });
  } catch (err) {
    console.warn("push notify failed", err);
  }
}
